import { isNonEmpty } from '@sgme/fp';
import type { IntlKey } from 'locales';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { htmlDecode } from 'utils/html-decode';

type LabelItem = { id?: string; label?: string };

export function useLabel<Item extends LabelItem>(
  item: Item | null | undefined,
  placeholder?: IntlKey,
): typeof placeholder extends undefined ? string | undefined : string {
  const { formatMessage } = useIntl();

  const res = useMemo(() => {
    if (isNonEmpty(item?.id) && isNonEmpty(item.label)) {
      return htmlDecode(`${item.label} (${item.id})`);
    }
    if (isNonEmpty(item?.label)) {
      return htmlDecode(item.label);
    }
    if (isNonEmpty(item?.id)) {
      return htmlDecode(item.id);
    }
    return placeholder ? formatMessage({ id: placeholder }) : undefined;
  }, [item, formatMessage, placeholder]);
  return res as typeof placeholder extends undefined ? string | undefined : string;
}

export function useSalesCounterpartLabel(
  salesCounterpart?: Nullable<ISalesCounterpart>,
  displayEliot: boolean = false,
  placeholder?: IntlKey,
): typeof placeholder extends undefined ? string | undefined : string {
  const labelItem = {
    id: displayEliot
      ? [salesCounterpart?.id, salesCounterpart?.eliotCode].filter(isNonEmpty).join(' - ')
      : salesCounterpart?.id,
    label: salesCounterpart?.label,
  };
  return useLabel(labelItem, placeholder);
}
function useToggleClassName(selectedValue?: unknown) {
  return selectedValue ? 'btn-discreet-info' : 'btn-flat-secondary';
}

export function useSalesCounterpartButton(
  selectedValue?: Nullable<ISalesCounterpart>,
  displayEliot: boolean = false,
  placeholder: IntlKey = 'form.Client.select',
) {
  const label = useSalesCounterpartLabel(selectedValue, displayEliot, placeholder);
  const className = useToggleClassName(selectedValue);
  return { label, className };
}
